import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { useLocation, useParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField
} from '@mui/material'
import Content from 'components/content/content'
import Filter from 'components/filter/filter'
import type { PartialOtherBrandUsersProps } from 'modules/partial'
import CONSTANT from 'modules/constant'
import type { ErrorProps, ParamProps, OtherBrandUsersListProps } from 'modules/types'
import {
  useDownloadOtherBrandUsersMutation,
  useGetOtherBrandUsersListMutation
} from 'store/otherBrandModel'
import Loading from 'components/loading/loading'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import {
  useGetCarModelDropdownListMutation,
  useGetYearsDropdownListByMutation
} from 'store/dropdown'
import GlobalStyle from 'modules/styles'
import { ActionButton } from 'components/action-button/action-button'

const OtherBrandUsers = () => {
  const params = useParams<ParamProps>()
  const locationState = useLocation()
  const [getOtherBrandUsersList, otherBrandUsersList] = useGetOtherBrandUsersListMutation()
  const [downloadOtherBrandUsers, download] = useDownloadOtherBrandUsersMutation()
  const [getCarModelDropdownList, model] = useGetCarModelDropdownListMutation()
  const [getYearsDropdownListBy, years] = useGetYearsDropdownListByMutation()

  const [dialogFilter, setDialogFilter] = useState<boolean>(false)

  const [filter, setFilter] = useState({
    year: '',
    carModel: locationState.state?.id || ''
  })
  const [payload, setPayload] = useState<PartialOtherBrandUsersProps>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    field:
      'brandName,modelName,year,userName,memberId,fullName,gender,dateOfBirth,mobilePhone,province,city,phoneNumber',
    search: { value: '' },
    discontinued: '',
    year: '',
    carModel: locationState.state?.id || ''
  })

  const onResync = () => {
    const formData = new FormData()
    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', payload.field)
    formData.append('car-model', payload.carModel)

    if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
    if (!isEmpty(payload.year)) formData.append('year', payload.year)
    if (!isEmpty(payload.discontinued)) formData.append('discontinued', payload.discontinued)

    getOtherBrandUsersList(formData)
  }

  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: { value } })
  const onFilter = (state: boolean) => setDialogFilter(state)

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', String(0))
    formData.append('length', CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString())
    formData.append('field', payload.field)
    formData.append('car-model', payload.carModel)

    if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
    if (!isEmpty(payload.year)) formData.append('year', payload.year)
    if (!isEmpty(payload.discontinued)) formData.append('discontinued', payload.discontinued)
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadOtherBrandUsers(formData)
  }

  useEffect(() => {
    onResync()
  }, [payload])

  const columns = [
    { id: 'brandName', label: 'Brand Name' },
    { id: 'modelName', label: 'Model Name' },
    { id: 'year', label: 'Year' },
    { id: 'userName', label: 'User Name' },
    { id: 'memberId', label: 'Member ID' },
    { id: 'fullName', label: 'Full Name' },
    { id: 'gender', label: 'Gender' },
    { id: 'dateOfBirth', label: 'Date Of Birth' },
    { id: 'mobilePhone', label: 'Mobile Phone' },
    { id: 'province', label: 'Province' },
    { id: 'city', label: 'City' },
    { id: 'phoneNumber', label: 'Phone Number' }
  ]

  const numbers = GLOBAL.tableNumber(payload.start)

  const totalPage =
    otherBrandUsersList.data && GLOBAL.tableTotalPage(otherBrandUsersList.data.recordsTotal)

  return (
    <>
      <Content
        title='Other Brand Users'
        tableHeadTitles={columns.map((col) => col.label)}
        onSearch={onSearch}
        onResync={onResync}
        onFilter={() => {
          getYearsDropdownListBy()
          onFilter(true)
        }}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={otherBrandUsersList.data && otherBrandUsersList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={otherBrandUsersList.isLoading}
        useBackButton
        useDownloadDropdown
      >
        {otherBrandUsersList.isSuccess &&
          otherBrandUsersList.data &&
          otherBrandUsersList.data.data.map((otherBrand: OtherBrandUsersListProps, index) => (
            <TableRow key={otherBrand.id}>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <ActionButton
                  menus={[
                    {
                      label: 'View',
                      action: () => {
                        console.log('here')
                      }
                    }
                  ]}
                />
              </TableCell>
              {columns.map((col) => {
                const value = otherBrand[col.id as keyof OtherBrandUsersListProps]
                return <TableCell key={col.id}>{String(value || '-')}</TableCell>
              })}
            </TableRow>
          ))}
      </Content>

      {otherBrandUsersList.isLoading && <Loading />}

      <Notification
        open={
          !otherBrandUsersList.isLoading &&
          !otherBrandUsersList.isUninitialized &&
          !otherBrandUsersList.isSuccess
        }
        onClose={() =>
          otherBrandUsersList.isError ? otherBrandUsersList.reset() : location.reload()
        }
        isError={Boolean(otherBrandUsersList.error) && otherBrandUsersList.isError}
        message={GLOBAL.returnExceptionMessage(
          otherBrandUsersList.isError,
          otherBrandUsersList.error as ErrorProps
        )}
      />
      <Filter
        open={dialogFilter}
        title='Filter'
        onClose={() => onFilter(false)}
        onCancel={() => onFilter(false)}
        isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
        onReset={() => {
          setFilter({
            year: '',
            carModel: ''
          })
          setPayload({
            start: 0,
            length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
            field:
              'brandName,modelName,year,userName,memberId,fullName,gender,dateOfBirth,mobilePhone,province,city,phoneNumber',
            search: { value: '' },
            discontinued: '',
            year: '',
            carModel: params.key || ''
          })
        }}
        onSubmit={() => {
          setPayload({
            ...payload,
            ...filter
          })
          onFilter(false)
        }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4 }}
        >
          <Autocomplete
            sx={{ flex: 1 }}
            options={(model && model.data) || []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) =>
              option && value ? option.name == value.name : false
            }
            onOpen={() => getCarModelDropdownList()}
            onChange={(_, model) => setFilter({ ...filter, carModel: model && String(model.id) })}
            value={
              (model &&
                model.data &&
                model.data.find((e) => String(e.id) == String(filter.carModel))) ||
              undefined
            }
            ListboxProps={GlobalStyle.ListBox}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                {item.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                name='Model'
                label='Model'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {model.isLoading && <CircularProgress color='inherit' size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <FormControl sx={{ flex: 1 }}>
            <InputLabel id='Year'>Year</InputLabel>
            <Select
              labelId='Year'
              id='Year'
              value={filter.year}
              label='Year'
              onChange={(event) => setFilter({ ...filter, year: event.target.value.toString() })}
            >
              {years.data &&
                years.isSuccess &&
                years.data.map((item, index) => (
                  <MenuItem value={item} key={index}>
                    {item}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Filter>
    </>
  )
}

export default OtherBrandUsers
